import { Segmented } from "antd";
import {
  AppstoreOutlined,
  DollarOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import usePermissions from "../../../hooks/usePermissions";

/**
 * Farmer Finance page / sidebar — three core permissions:
 * - vendors.finance  → View Farmers Finance (Reports overview)
 * - vendors.payment  → Pay Farmers (Pay farmer tab + Pay & print)
 * - vendors.settle   → Clear / Archive (archives page, reachable via /farmer-settlement-archives)
 *
 * Charges tab uses separate expense permissions (not these three).
 */
export const FARMER_FINANCE_ACCESS = [
  "vendors.finance",
  "vendors.settle",
  "vendors.payment",
];

export function useFarmerFinanceTabAccess() {
  const { hasPermission, hasAny } = usePermissions();

  // Reports: view finance, or payment (needed to run Pay & print on the report)
  const canOverview = hasAny(["vendors.finance", "vendors.payment"]);
  // Pay & print button on Reports
  const canPayAndPrint = hasPermission("vendors.payment");
  // Pay farmer tab — amounts to pay
  const canPayments = hasPermission("vendors.payment");
  // Charges / expense types — own permissions (not finance/payment/settle)
  const canViewExpenses = hasAny([
    "vendors.view_expenses",
    "vendors.expenses",
    "vendors.expenses_add",
    "vendors.expenses_cancel",
  ]);
  const canAddExpenses = hasAny(["vendors.expenses_add", "vendors.expenses"]);
  const canManageExpenseTypes = hasAny([
    "vendors.expense_types_view",
    "vendors.expense_types_add",
    "vendors.expense_types_edit",
    "vendors.expense_types_delete",
  ]);
  const canCharges = canViewExpenses || canManageExpenseTypes;

  const allowed = [
    canOverview && "overview",
    canCharges && "charges",
    canPayments && "payments",
  ].filter(Boolean);

  return {
    canOverview,
    canPayAndPrint,
    canCharges,
    canViewExpenses,
    canAddExpenses,
    canPayments,
    allowedTabs: allowed,
    defaultTab: allowed[0] || "overview",
  };
}

/** Compact segmented control for switching between `/farmer-settlements` sections. */
export default function FarmerFinanceTabs({ activeKey = "overview", onChange }) {
  const { t } = useTranslation();
  const { canOverview, canCharges, canPayments } = useFarmerFinanceTabAccess();

  const options = [
    canOverview && {
      value: "overview",
      label: (
        <span className="fs-segmented-label">
          <AppstoreOutlined />
          {t("farmerAccountsOverview", "Reports")}
        </span>
      ),
    },
    canCharges && {
      value: "charges",
      label: (
        <span className="fs-segmented-label">
          <DollarOutlined />
          {t("farmerAccountsCharges", "Charges")}
        </span>
      ),
    },
    canPayments && {
      value: "payments",
      label: (
        <span className="fs-segmented-label">
          <WalletOutlined />
          {t("farmerAccountsPay", "Pay farmer")}
        </span>
      ),
    },
  ].filter(Boolean);

  if (options.length < 2) return null;

  return (
    <div className="fs-segmented-wrap">
      <Segmented
        className="fs-segmented"
        value={activeKey}
        onChange={onChange}
        options={options}
        size="large"
      />
    </div>
  );
}
